import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import withStyles from 'react-jss';
import _ from 'lodash';
import { withRouter, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getAuthState,
  getOnboardingState,
  showOnlineDegreesOnly,
} from '../../store/selectors';
import {
  addCareerPathChoice,
  toggleCareerPathOnlineDegreesOnly,
} from '../../store/actions';
import LearnMoreLink from './utility/LearnMoreLink';
import { fireGTM } from '../../gtm/gtmUtil';
import { degreePairingPage_GTM } from '../../gtm/datalayers/degreePairingDataLayers.json';
import ToggleButtonModality, {
  isOnline,
  ModalityIcons,
} from '../utility/ToggleButtonModality';
import DegreeFilters from '../utility/DegreeFilters';
import BoxSecondaryUnderline from '../AdvisorDashboard/basic-style-components/BoxSecondaryUnderline';
import FuzzySearchBar from '../career-search/FuzzySearchBar';
import AcademicProgramCategoryFilter from '../utility/AcademicProgramCategoryFilter';

const selectDegreeButtonStyles = {
  color: (props) => ({
    backgroundColor: 'white',
    border: `1px solid black`,
    color: 'black',
    borderStyle: 'solid',

    '&:hover': {
      backgroundColor: props.tenant.primary,
      borderColor: props.tenant.primary,
      color: 'white',
    },
  }),
};

function CareerPathList(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const careerSelected = (degreeChoices, degree, career) => {
    // We need to avoid any possible race conditions on this object
    const tempSelectPathObj = Object.assign(
      {},
      degreePairingPage_GTM.selectPath
    );

    tempSelectPathObj.component = career;
    tempSelectPathObj.section = degree.titleEn;
    // Send off to Google Tag Manager
    fireGTM(tempSelectPathObj);

    // First check if we need to increment the total number of paths chosen count
    const currentNumPathsChosen = Object.values(degreeChoices).filter(
      (degreeChoice) => {
        return degreeChoice.id;
      }
    );

    // Find out which tab is 'active' and set that career title when user presses '+ select path'
    // Figure out if any tabs are missing a career-path and open to that next
    let nextTabToOpen;
    let keepListOpen = true;

    switch (props.onboarding.currentTab) {
      case 1:
        if (_.isEmpty(props.onboarding.degreeChoices[2])) {
          nextTabToOpen = 2;
        } else if (_.isEmpty(props.onboarding.degreeChoices[3])) {
          nextTabToOpen = 3;
        } else {
          nextTabToOpen = 0;
          keepListOpen = false;
        }

        break;
      case 2:
        if (_.isEmpty(props.onboarding.degreeChoices[3])) {
          nextTabToOpen = 3;
        } else if (_.isEmpty(props.onboarding.degreeChoices[1])) {
          nextTabToOpen = 1;
        } else {
          nextTabToOpen = 0;
          keepListOpen = false;
        }

        break;
      case 3:
        if (_.isEmpty(props.onboarding.degreeChoices[1])) {
          nextTabToOpen = 1;
        } else if (_.isEmpty(props.onboarding.degreeChoices[2])) {
          nextTabToOpen = 2;
        } else {
          nextTabToOpen = 0;
          keepListOpen = false;
        }

        break;
      default:
        nextTabToOpen = 0;
        keepListOpen = false;
    }

    // User selected a specific path from the list so update the redux store with: career info, remove list bool, and current tab to update
    dispatch({
      type: 'SET_CAREER_TAB',
      payload: {
        careerInfo: {
          ...props.onboarding.degreeChoices,
          [props.onboarding.currentTab]: degree,
        },
        careerTabSelected: keepListOpen,
        currentTab: nextTabToOpen,
        pathsChosenOverall: currentNumPathsChosen.length + 1,
      },
    });

    const singlePathSelectedObject = {
      degreeId: degree.id,
      careerId: props.onboarding.careerChoices[props.onboarding.currentTab].id,
    };

    // Need to send careerId, degreeId, and chosen order to b/e for keeping in the DB
    props.addCareerPathChoice(
      props.tenant.id,
      singlePathSelectedObject,
      props.auth.jwtToken
    );
  };

  const getAcademicProgramCategory = (degree) => {
    const certificateType = ['Certificate'];
    const gradCertificateType = ['Graduate Certificate'];
    const undergradDegreeTypes = ['Associate', "Bachelor's", 'Minor'];
    const gradDegreeTypes = ["Master's", 'Doctorate'];

    let academicProgramCategory;
    if (certificateType.includes(degree.academicProgramTypeEn)) {
      academicProgramCategory = 'Certificates';
    } else if (gradCertificateType.includes(degree.academicProgramTypeEn)) {
      academicProgramCategory = 'Graduate Certificates';
    } else if (undergradDegreeTypes.includes(degree.academicProgramTypeEn)) {
      academicProgramCategory = 'Undergraduate Degrees';
    } else if (gradDegreeTypes.includes(degree.academicProgramTypeEn)) {
      academicProgramCategory = 'Graduate Degrees';
    }

    return academicProgramCategory;
  };

  // Sort the list of degrees
  const careerListArray = (careerListArrayProps) => {
    if (careerListArrayProps.onboarding.currentTab === 0) {
      return [];
    }
    return _.orderBy(
      careerListArrayProps.onboarding.userData.degreeCareerIdMap[
        careerListArrayProps.onboarding.careerChoices[
          careerListArrayProps.onboarding.currentTab
        ].id
      ],
      ['titleEn', 'academicProgramTypeEn']
    );
  };

  const [degreeFilter, setDegreeFilter] = useState({
    visibleColleges: [],
    visibleDegrees: [],
    visibleLocations: [],
  });

  // Initialize searchedDegrees to be the list of all degrees
  // or searching doesn't work right
  const [searchedDegrees, setSearchedDegrees] = useState(
    careerListArray(props)
  );

  const [
    selectedAcademicProgramCategories,
    setSelectedAcademicProgramCategories,
  ] = useState(['View All']);

  const [results, setResults] = useState([]);

  useEffect(
    () => {
      // Baobab only has a search bar option (for now), so don't filter on the other things
      /* eslint-disable-next-line */
      props.tenant.name !== 'Baobab'
        ? setResults(
            // For each degree of the search results (will either be all degrees at first or a subset of a search)...
            searchedDegrees.filter((degree) => {
              // Check if its list of institutions includes one or more of the
              // colleges that have been selected in the degree filters
              const matchesSchools = degree.institution_info.some((inst) =>
                degreeFilter.visibleColleges.includes(inst.institution_name)
              );

              // Check if its list of campuses includes one or more of the campuses
              // that have been selected in the degree filters
              const matchesLocations = degree.campus
                .split(',')
                .flat()
                .some((location) =>
                  degreeFilter.visibleLocations.includes(location)
                );

              // Check if its degree type matches the degree type that has been selected
              // in the degree filters
              const matchesDegreeTypes = degreeFilter.visibleDegrees.includes(
                degree.degreeTypeEn
              );

              // Check if its academic program category matches the one that has been
              // selected in the academic program categroy filter
              let matchesAcademicProgramCategories;
              if (selectedAcademicProgramCategories.includes('View All')) {
                matchesAcademicProgramCategories = true;
              } else {
                matchesAcademicProgramCategories =
                  selectedAcademicProgramCategories.includes(
                    getAcademicProgramCategory(degree)
                  );
              }

              // Check if the degree should be shown based on if the modality toggle
              // is set to only online or on campus and online
              let matchesModality;
              const degreeIsOnline = isOnline(
                degree && degree.institution_info && degree.institution_info[0]
              );
              if (props.showOnlineDegreesOnly) {
                matchesModality = degreeIsOnline;
              } else {
                matchesModality = true;
              }

              // Finally, return true if the degree meets all filter requirements
              return (
                matchesSchools &&
                matchesLocations &&
                matchesDegreeTypes &&
                matchesAcademicProgramCategories &&
                matchesModality
              );
            })
          )
        : setResults(searchedDegrees);
    },
    // Do this ^ if any of the various filters change
    [
      degreeFilter,
      selectedAcademicProgramCategories,
      props.showOnlineDegreesOnly,
      searchedDegrees,
    ]
  );

  // Reset the list of searched degrees if the tab changes
  useEffect(() => {
    setSearchedDegrees(careerListArray(props));
  }, [props.onboarding.currentTab, props.currentLocale]);

  // If no tab is open, don't bother going on to generating the list of degrees
  // or rendering this component at all
  if (props.onboarding.currentTab === 0) {
    return null;
  }

  // Else, generate the list of degrees based on the filtered/searched results
  const careerList = results.map((degree, index) => {
    if (degree.inactive) return null;

    const uniqueKey = degree.titleEn.charCodeAt(0) * index;
    const institutionList = degree.institution_info
      .map((institution) => {
        return institution.institution_name;
      })
      .join(', ');

    let academicProgramCategory;
    let academicProgramCategoryStyle = {
      backgroundColor: '#D0D0D0',
      color: 'black',
    };
    const certificateType = ['Certificate'];
    const gradCertificateType = ['Graduate Certificate'];
    const degreeTypes = ['Associate', "Bachelor's", "Master's", 'Doctorate'];
    const minorType = ['Minor'];
    if (certificateType.includes(degree.academicProgramTypeEn)) {
      academicProgramCategory = 'Certificate';
      academicProgramCategoryStyle = {
        backgroundColor: props.tenant.secondary,
        color: 'black',
      };
    } else if (gradCertificateType.includes(degree.academicProgramTypeEn)) {
      academicProgramCategory = 'Graduate Certificate';
      academicProgramCategoryStyle = {
        backgroundColor: props.tenant.secondary,
        color: 'black',
      };
    } else if (degreeTypes.includes(degree.academicProgramTypeEn)) {
      academicProgramCategory = 'Degree';
      academicProgramCategoryStyle = {
        backgroundColor: 'black',
        color: 'white',
      };
    } else if (minorType.includes(degree.academicProgramTypeEn)) {
      academicProgramCategory = 'Minor';
      academicProgramCategoryStyle = {
        backgroundColor: 'black',
        color: 'white',
      };
    }

    return (
      <>
        <div
          className="career-list-item"
          data-cy="career-list-item"
          key={uniqueKey}
        >
          <BoxSecondaryUnderline tenant={props.tenant}>
            <div className="degree-text-select-path" data-cy="degree-element">
              <div className="container-1">
                <div className="degree-text">
                  <p className="visually-hidden">
                    {`Option
                    ${index + 1}
                    for
                    ${
                      props.onboarding.careerChoices[
                        props.onboarding.currentTab
                      ].titleEn
                    }`}
                  </p>
                  <p className="visually-hidden">
                    Program Category:
                    {degree.academicProgramType}
                  </p>
                  <div
                    className="degree-text-program-category"
                    style={academicProgramCategoryStyle}
                    aria-hidden
                  >
                    {degree.academicProgramType}
                  </div>
                  <div className="degree-text-header">
                    <p className="visually-hidden">
                      Title:
                      {degree.titleEn}
                    </p>
                    <span className="degree-text-header-text" aria-hidden>
                      {degree.titleEn}
                    </span>
                    {props.tenant.name !== 'Baobab' && (
                      <ModalityIcons
                        className="degree-text-header-icon"
                        degree={degree}
                      />
                    )}
                  </div>
                  <p className="visually-hidden">
                    Academic Program type:
                    {degree.degreeTypeEn}
                  </p>
                  <p
                    className="degree-text-type"
                    data-cy="degree-type-en"
                    aria-hidden
                  >
                    {degree.degreeTypeEn}
                  </p>
                  {props.tenant.name !== 'Baobab' && (
                    <>
                      <p className="visually-hidden">{`This ${academicProgramCategory} is offered at the following institutions: ${institutionList}`}</p>
                      <p
                        className="degree-text-location e"
                        data-cy="degree-text-location"
                        aria-hidden
                      >
                        {institutionList}
                      </p>
                    </>
                  )}
                </div>
                {(props.tenant.name === 'ASU' || props.tenant.name.toLowerCase() === 'pitt') && (
                  <>
                    <p className="visually-hidden">{`This ${academicProgramCategory} is offered at the following campuses: ${degree.campus}`}</p>
                    <p className="degree-text-type" aria-hidden>
                      {degree.campus}
                    </p>
                  </>
                )}
                <Link
                  to={{
                    pathname: props.location.pathname,
                    search: `degree=${encodeURIComponent(
                      degree.titleEn
                    )} degreeId ${degree.id}`,
                  }}
                  aria-label={`click to open more info about ${degree.titleEn}`}
                >
                  <LearnMoreLink
                    tenant={props.tenant}
                    data-cy="learn-more-link"
                    primary={props.tenant.primary}
                    section={degree.titleEn}
                    text={`click to open more info about ${degree.titleEn}`}
                    component={
                      props.onboarding.careerChoices[
                        props.onboarding.currentTab
                      ].titleEn
                    }
                    modalImage={degree.career_image}
                    ariaLabel={`click to open more info about ${degree.titleEn}`}
                  />
                </Link>
              </div>
              <div className="select-path-button__container">
                <button
                  className={['select-path-text', props.classes.color].join(
                    ' '
                  )}
                  onClick={() =>
                    careerSelected(
                      props.onboarding.degreeChoices,
                      degree,
                      props.onboarding.careerChoices[
                        props.onboarding.currentTab
                      ].titleEn
                    )
                  }
                  onKeyPress={() =>
                    careerSelected(
                      props.onboarding.degreeChoices,
                      degree,
                      props.onboarding.careerChoices[
                        props.onboarding.currentTab
                      ].titleEn
                    )
                  }
                  type="button"
                  tabIndex={0}
                  style={selectDegreeButtonStyles}
                  data-cy="select-path-text"
                  aria-label={`Select path ${degree.titleEn} to pair with ${
                    props.onboarding.careerChoices[props.onboarding.currentTab]
                      .titleEn
                  }`}
                >
                  {`+ ${t('Select path', { context: 'action' })}`}
                </button>
              </div>
            </div>
          </BoxSecondaryUnderline>
        </div>
      </>
    );
  });

  return (
    <>
      <div
        className="content career-list-container"
        data-cy="career-list-container"
      >
        <div className="degree-container-header">
          <div className="degree-container-header__chunk-container degree-container-header__chunk-container-left">
            {props.tenant.name === 'ASU' && (
              <AcademicProgramCategoryFilter
                color={props.tenant.primary}
                setSelectedAcademicProgramCategories={
                  setSelectedAcademicProgramCategories
                }
                educationLevel={props.onboarding.userData.user.educationLevel}
              />
            )}
            {props.tenant.name !== 'Baobab' && (
              <ToggleButtonModality
                className="modality-toggle"
                color={props.tenant.primary}
                value={props.showOnlineDegreesOnly}
                onClick={props.toggleCareerPathOnlineDegreesOnly}
                disabled={props.tenant.name === 'InStride'}
                ariaLabel="Turn on to display academic offerings that have online options. Turn off to display academic offerings that are offered online or in person"
              />
            )}
          </div>
          <div className="degree-container-header__chunk-container degree-container-header__chunk-container-right">
            {props.tenant.name !== 'Baobab' && (
              <DegreeFilters
                color={props.tenant.primary}
                degrees={careerListArray(props)}
                setDegreeFilter={setDegreeFilter}
                degreesShowing={results.length}
                tenantName={props.tenant.name}
              />
            )}
            <div className="career-searchbar__container">
              <FuzzySearchBar
                inputText={t('Search', { context: 'action' })}
                tenant={props.tenant}
                listToSearch={careerListArray(props)}
                sendFilteredListToParent={setSearchedDegrees}
                setSearchListLoadingOnParent={() => {}}
                overRideStylesSearchBar="degree-search-bar"
              />
            </div>
          </div>
        </div>
        <div className="career-list-container-content">
          {careerList.length > 0 ? (
            <div
              aria-live="assertive"
              aria-atomic="true"
              aria-relevant="all"
              role="alert"
            >
              <p className="visually-hidden">
                {`Academic offerings for ${
                  props.onboarding.careerChoices[props.onboarding.currentTab]
                    .titleEn
                }`}
              </p>
              {careerList}
            </div>
          ) : (
            <div className="no-matches" aria-live="assertive" role="alert">
              {selectedAcademicProgramCategories.includes('View All') ? (
                <p>
                  No degrees match your selected filters. Replace your current
                  career choice to move forward.
                </p>
              ) : (
                <p>No degrees match your selected filters.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const mapStateToProps = (state) => ({
  onboarding: getOnboardingState(state),
  auth: getAuthState(state),
  showOnlineDegreesOnly: showOnlineDegreesOnly(state),
  currentLocale: state.currentLocale,
});

const mapDispatchToProps = {
  addCareerPathChoice,
  toggleCareerPathOnlineDegreesOnly,
};

export default withStyles(selectDegreeButtonStyles)(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(CareerPathList))
);
